import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CreateEditBusinessUnitModal from "./createEditBusinessUnit";
import LicenseToggleIcon from "./LicenseToggleIcon";
import UserLicensesModal from "./userLicensesModal";
import {
  actionsColumn,
  textColumn
} from "../../components/tableComponent/columns";
import TableComponent from "../../components/tableComponent";
import "./styles.css";
import {
  businessUnitCreateOrUpdate,
  callApiAsync,
  deleteBussinesUnit,
  getBusinessUnits
} from "../../services/apiService";
import AlertComponent from "../../components/alertModal";
import ToastNotification from "../../components/toastNotification";
import ButtonComponent from "../../components/button";
import icons from "../../constants/icons";
import StatusPill from "../../components/statusPill";

const BusinessUnit = () => {
  const userData = useSelector((state) => state.user?.userData);
  const [bussinesUnit, setBussinesUnit] = useState([]);
  const [openCreateEdit, setOpenCreateEdit] = useState(false);
  const [openUserLicenses, setOpenUserLicenses] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [alertOpen, setAlertOpen] = useState(false);
  const [suspendAlertOpen, setSuspendAlertOpen] = useState(false);
  const [pendingSuspendRow, setPendingSuspendRow] = useState(null);

  const [toastMessage, setToastMessage] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("warning");

  const getBusinessUnitTable = () => {
    callApiAsync(getBusinessUnits, (data) => {
      setBussinesUnit(data);
    });
  };

  useEffect(() => {
    getBusinessUnitTable();
  }, []);

  const createUpdateBussinessUnit = async (businessUnit) => {
    try {
      await callApiAsync(
        () => businessUnitCreateOrUpdate(businessUnit),
        () => {
          setToastSeverity("success");
          setToastMessage("Unidad de negocio guardada exitosamente.");
          setToastOpen(true);
          getBusinessUnitTable();
        },
        (error) => {
          setToastSeverity("error");
          setToastMessage(error);
          setToastOpen(true);
        }
      );
    } catch (error) {
      const errorMessage = error?.message || error;
      setToastSeverity("error");
      setToastMessage(errorMessage);
      setToastOpen(true);
    } finally {
      handleClose();
    }
  };

  const updateLabLicense = (row, newStatus) => {
    callApiAsync(
      () =>
        businessUnitCreateOrUpdate({
          id: row.id,
          name: row.name,
          idBillingUnit: row.idBillingUnit,
          idCountry: row.idCountry,
          status: newStatus,
          enterpriseKey: row.enterpriseKey,
          csmId: row.csmId ?? null,
          email: userData?.email ?? ""
        }),
      () => {
        setToastSeverity("success");
        setToastMessage(
          newStatus === 1 ? "Licencia del lab activada." : "Licencia del lab suspendida."
        );
        setToastOpen(true);
        getBusinessUnitTable();
      },
      (err) => {
        const errorMessage = err?.message || err;
        setToastSeverity("error");
        setToastMessage(errorMessage);
        setToastOpen(true);
      }
    );
  };

  const handleLicenseToggle = (row) => {
    if (row.status === 1) {
      setPendingSuspendRow(row);
      setSuspendAlertOpen(true);
    } else {
      updateLabLicense(row, 1);
    }
  };

  const confirmSuspendLicense = () => {
    if (!pendingSuspendRow) return;
    updateLabLicense(pendingSuspendRow, 0);
    setSuspendAlertOpen(false);
    setPendingSuspendRow(null);
  };

  const deleteBussinesUnitBtn = (featureData) => {
    callApiAsync(
      () => deleteBussinesUnit(featureData.id),
      () => {
        setToastMessage("se elimino la unidad de negocio exitosamente.");
        setToastSeverity("success");
        setToastOpen(true);
        getBusinessUnitTable();
      },
      (err) => {
        const errorMessage = err?.message || err;
        setToastSeverity("error");
        setToastMessage(errorMessage);
        setToastOpen(true);
      }
    );
  };

  const handleCreateEdit = (row) => {
    if (!row) {
      setSelectedRow({});
      setOpenCreateEdit(true);
      return;
    }
    setSelectedRow(row);
    setOpenCreateEdit(true);
  };

  const handleClose = () => {
    setOpenCreateEdit(false);
  };

  const handleDelete = () => {
    deleteBussinesUnitBtn(selectedRow);
    setSelectedRow({});
  };

  const actionBtn = ({ row }) => {
    const isActive = row.original.status === 1;
    const tenantSuspended =
      (row.original.billingUnitStatus ?? row.original.BillingUnitStatus) !== 1;

    return (
      <div className="HContainer">
        <ButtonComponent
          onClick={() => handleLicenseToggle(row.original)}
          variant="contained"
          backgroundColor="#fff0"
          title={
            tenantSuspended
              ? `${isActive ? "Suspender" : "Activar"} licencia (tenant suspendido — no entrará al login hasta reactivar el tenant)`
              : isActive
                ? "Suspender licencia de este lab"
                : "Activar licencia de este lab"
          }
        >
          <LicenseToggleIcon licenseActive={isActive} />
        </ButtonComponent>
        <ButtonComponent
          onClick={() => {
            setSelectedRow(row.original);
            setOpenUserLicenses(true);
          }}
          variant="contained"
          backgroundColor="#fff0"
          title="Licencias por usuario"
        >
          <img className="btn-icon" src={icons.blue_user} alt="Usuarios" />
        </ButtonComponent>
        <ButtonComponent
          onClick={() => handleCreateEdit(row.original)}
          variant="contained"
          backgroundColor="#fff0"
        >
          <img className="btn-icon" src={icons.blue_edit} alt="icon" />
        </ButtonComponent>
      </div>
    );
  };

  const columns = useMemo(
    () => [
      textColumn("billingUnitName", "Unidad de facturación"),
      {
        id: "billingUnitStatus",
        header: "Tenant",
        size: 100,
        Cell: ({ row }) => {
          const isActive =
            (row.original.billingUnitStatus ?? row.original.BillingUnitStatus) === 1;
          return (
            <StatusPill
              backgroundColor={isActive ? "#4ADE80" : "#FCA5A5"}
              textColor="#000000"
            >
              {isActive ? "Activo" : "Suspendido"}
            </StatusPill>
          );
        }
      },
      textColumn("name", "Nombre de la Unidad de negocio", 200),
      textColumn("csmName", "CSM"),
      textColumn("countryName", "País", 100),
      textColumn("enterpriseName", "Unidad de negocio sistema"),
      {
        id: "status",
        header: "Licencia",
        size: 100,
        Cell: ({ row }) => (
          <StatusPill
            backgroundColor={row.original.status === 1 ? "#4ADE80" : "#D9D9D9"}
            textColor="#000000"
          >
            {row.original.status === 1 ? "Activa" : "Inactiva"}
          </StatusPill>
        )
      },
      actionsColumn(actionBtn, 130, "Acciones")
    ],
    []
  );

  return (
    <div>
      <h1>Unidad de negocio</h1>
      <p style={{ marginBottom: 16, color: "#555", maxWidth: 720 }}>
        Gestione labs y su licencia para Intelapp. Use el icono verde/rojo para activar o
        suspender cada lab sin afectar a los demás del mismo tenant.
      </p>
      <div>
        <TableComponent
          columns={columns}
          data={bussinesUnit}
          enableGlobalFilter={true}
          initialState={{
            sorting: [
              {
                id: "billingUnitName",
                desc: false
              }
            ]
          }}
          headerButtons={() => (
            <ButtonComponent
              onClick={() => handleCreateEdit()}
              type="submit"
              variant="contained"
              backgroundColor="#F6C079"
              textColor="#000000"
              margin="15px 0"
            >
              <div className="HContainer a-center">
                <img
                  className="btn-icon icon-margin-r-15"
                  src={icons.plus}
                  alt="icon"
                />
                <p>Crear unidad de negocio</p>
              </div>
            </ButtonComponent>
          )}
        />
      </div>
      <CreateEditBusinessUnitModal
        tableData={bussinesUnit}
        rowData={selectedRow}
        open={openCreateEdit}
        onClose={handleClose}
        onSubmitFuntion={createUpdateBussinessUnit}
      />
      <UserLicensesModal
        open={openUserLicenses}
        onClose={() => setOpenUserLicenses(false)}
        businessUnitRow={selectedRow}
      />
      <AlertComponent
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={handleDelete}
        title="Confirmar Eliminación"
        message="¿Estás seguro de que deseas eliminar esta unidad de negocio? Esta acción no se puede deshacer."
      />
      <AlertComponent
        open={suspendAlertOpen}
        onClose={() => {
          setSuspendAlertOpen(false);
          setPendingSuspendRow(null);
        }}
        onConfirm={confirmSuspendLicense}
        title="Suspender licencia del lab"
        message={`¿Inactivar la licencia de "${pendingSuspendRow?.name}"? Este lab dejará de aparecer en el login de Intelapp.`}
        severity="danger"
      />
      <ToastNotification
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
        message={toastMessage}
        severity={toastSeverity}
      />
    </div>
  );
};

export default BusinessUnit;
